import { emptySplitApi as baseApi } from "./baseApi";

const api = baseApi.enhanceEndpoints({
  addTagTypes: [
    "ProTrackMilestone",
    "ProTrackSnag",
    "ProTrackHandover",
    "ProTrackDashboard",
    "ProTrackProject",
    "ProTrackPortfolio",
  ] as const,
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type MilestoneCategory =
  | "FOUNDATION"
  | "SUPERSTRUCTURE"
  | "ROOFING"
  | "BRICKWORK"
  | "PLASTERING"
  | "ELECTRICAL"
  | "PLUMBING"
  | "FINISHES"
  | "LANDSCAPING"
  | "COMMISSIONING"
  | "OTHER";

export type MilestoneStatus =
  | "PLANNED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "DELAYED"
  | "CANCELLED";

export interface ProTrackMilestone {
  id?: string;
  blockId: string;
  blockName?: string;
  title: string;
  category: MilestoneCategory;
  plannedStart: string;
  plannedEnd: string;
  actualStart?: string;
  actualEnd?: string;
  status: MilestoneStatus;
  progressPct: number;
  responsible?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type SnagCategory =
  | "STRUCTURAL"
  | "ELECTRICAL"
  | "PLUMBING"
  | "FINISHES"
  | "DOORS_WINDOWS"
  | "ROOFING"
  | "EXTERNAL"
  | "OTHER";

export type SnagSeverity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type SnagStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "RESOLVED"
  | "VERIFIED"
  | "CLOSED";

export interface ProTrackSnag {
  id?: string;
  blockId: string;
  blockName?: string;
  siteName?: string;
  unitNumber?: string;
  title: string;
  description?: string;
  category: SnagCategory;
  severity: SnagSeverity;
  status: SnagStatus;
  reportedDate: string;
  dueDate?: string;
  resolvedDate?: string;
  reinspectionDate?: string;
  reportedBy?: string;
  assignedTo?: string;
  subcontractor?: string;
  location?: string;
  trade?: string;
  rootCause?: string;
  actionRequired?: string;
  inspectionType?: "INITIAL" | "RE_INSPECTION" | "FINAL_HANDOVER" | "FOLLOW_UP";
  closureComment?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type HandoverStatus =
  | "SCHEDULED"
  | "INSPECTED"
  | "SNAGGED"
  | "SIGNED_OFF"
  | "KEYS_HANDED";

export interface ProTrackHandover {
  id?: string;
  blockId: string;
  blockName?: string;
  unitNumber: string;
  unitType?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  scheduledDate?: string;
  inspectionDate?: string;
  handoverDate?: string;
  status: HandoverStatus;
  snagCount: number;
  outstandingSnags: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProTrackBlockSummary {
  blockId: string;
  blockCode: string;
  blockName: string;
  development?: string;
  milestones: number;
  milestoneProgress: number;
  delayedMilestones: number;
  openSnags: number;
  handovers: number;
  keysHanded: number;
}

export interface ProTrackDashboard {
  blocks: number;
  milestones: {
    total: number;
    completed: number;
    inProgress: number;
    delayed: number;
  };
  snags: {
    total: number;
    open: number;
    resolved: number;
    critical: number;
  };
  handovers: {
    total: number;
    keysHanded: number;
    signedOff: number;
    snagged: number;
    scheduled: number;
  };
  blockSummary: ProTrackBlockSummary[];
}

// ── CTC Project (6-stage lifecycle) ──────────────────────────────────────────

export type ContractStatus =
  | "DRAFT"
  | "PENDING_SIGN"
  | "ACTIVE"
  | "AMENDED"
  | "TERMINATED";
export type ConstructionStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "ON_HOLD"
  | "DELAYED"
  | "PRACTICAL_COMPLETION"
  | "COMPLETE";
export type FinancialStatus =
  | "CURRENT"
  | "PAYMENT_DUE"
  | "OVERDUE"
  | "IN_DISPUTE"
  | "SETTLED";
export type SalesStatus = "PRE_LAUNCH" | "SELLING" | "SOLD_OUT" | "SLOW_MOVING";
export type DebtorsStatus =
  | "CURRENT"
  | "30_DAYS"
  | "60_DAYS"
  | "90_DAYS_PLUS"
  | "LEGAL_ACTION"
  | "WRITTEN_OFF";
export type ConveyancingStatus =
  | "NOT_INITIATED"
  | "DOCS_GATHERING"
  | "LODGED"
  | "IN_DEEDS_OFFICE"
  | "REGISTERED"
  | "COMPLETE";
export type ProjectSite = "MH" | "POMONA" | "PRET1" | "PRET2";
export type ProjectOverallStatus = "ACTIVE" | "ON_HOLD" | "CLOSED";

export interface ContractStage {
  status: ContractStatus;
  startDate?: string;
  estimatedCompletionDate?: string;
  contractValue?: number;
  contractValueUsd?: number;
  contractorName?: string;
  contractorContact?: string;
}

export interface ConstructionStage {
  status: ConstructionStatus;
  costToCompletion?: number;
  workDoneToDate?: number;
  balanceToFinish?: number;
  percentComplete?: number;
  plannedCompletion?: string;
  actualCompletion?: string;
}

export interface FinancialStage {
  status: FinancialStatus;
  lastPaymentDate?: string;
  outstandingPayment?: number;
  cashOnHand?: number;
  nostroOnHand?: number;
  monthlyExpectedPayments?: number;
}

export interface SalesStage {
  status: SalesStatus;
  totalSalesToDate?: number;
  totalUnits?: number;
  unitsSold?: number;
  unsoldUnitsCount?: number;
  unsoldStockValue?: number;
  salesVelocityPerMonth?: number;
}

export interface DebtorsStage {
  status: DebtorsStatus;
  debtorsPaidToDate?: number;
  outstandingDebtors?: number;
  lastPaymentDate?: string;
  monthlyExpectedCollections?: number;
  daysOutstanding?: number;
}

export interface ConveyancingStage {
  status: ConveyancingStatus;
  startDate?: string;
  completionDate?: string;
  attorney?: string;
  unitsRegistered?: number;
  unitsPendingTransfer?: number;
}

export interface ProTrackProject {
  id?: string;
  projectNumber: string;
  name: string;
  siteId: ProjectSite;
  overallStatus: ProjectOverallStatus;
  contract?: ContractStage;
  construction?: ConstructionStage;
  financial?: FinancialStage;
  sales?: SalesStage;
  debtors?: DebtorsStage;
  conveyancing?: ConveyancingStage;
  notes?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PortfolioSiteSummary {
  site: string;
  projectCount: number;
  totalCtc: number;
  totalWorkDone: number;
  avgConstructionPct: number;
  totalSales: number;
  totalOutstandingDebtors: number;
  totalNostroUsd: number;
}

export interface ProTrackPortfolio {
  sites: PortfolioSiteSummary[];
  totals: {
    projects: number;
    totalCtc: number;
    totalWorkDone: number;
    totalSales: number;
    totalOutstandingDebtors: number;
    totalNostroUsd: number;
    totalCashOnHand: number;
  };
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

const protrackApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Dashboard ──────────────────────────────────────────────────────────
    getProTrackDashboard: build.query<ProTrackDashboard, void>({
      query: () => "/protrack/dashboard",
      providesTags: ["ProTrackDashboard"],
    }),

    // ── Milestones ─────────────────────────────────────────────────────────
    getMilestones: build.query<ProTrackMilestone[], { blockId?: string }>({
      query: ({ blockId }) =>
        blockId
          ? `/protrack/milestones?blockId=${blockId}`
          : "/protrack/milestones",
      providesTags: ["ProTrackMilestone"],
    }),
    createMilestone: build.mutation<
      ProTrackMilestone,
      Omit<ProTrackMilestone, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/protrack/milestones", method: "POST", body }),
      invalidatesTags: ["ProTrackMilestone", "ProTrackDashboard"],
    }),
    updateMilestone: build.mutation<
      void,
      { id: string; data: Partial<ProTrackMilestone> }
    >({
      query: ({ id, data }) => ({
        url: `/protrack/milestones/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ProTrackMilestone", "ProTrackDashboard"],
    }),
    deleteMilestone: build.mutation<void, string>({
      query: (id) => ({ url: `/protrack/milestones/${id}`, method: "DELETE" }),
      invalidatesTags: ["ProTrackMilestone", "ProTrackDashboard"],
    }),

    // ── Snags ──────────────────────────────────────────────────────────────
    getSnags: build.query<
      ProTrackSnag[],
      { blockId?: string; status?: string }
    >({
      query: ({ blockId, status }) => {
        const params = new URLSearchParams();
        if (blockId) params.set("blockId", blockId);
        if (status) params.set("status", status);
        const qs = params.toString();
        return qs ? `/protrack/snags?${qs}` : "/protrack/snags";
      },
      providesTags: ["ProTrackSnag"],
    }),
    createSnag: build.mutation<
      ProTrackSnag,
      Omit<ProTrackSnag, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/protrack/snags", method: "POST", body }),
      invalidatesTags: ["ProTrackSnag", "ProTrackDashboard"],
    }),
    updateSnag: build.mutation<
      void,
      { id: string; data: Partial<ProTrackSnag> }
    >({
      query: ({ id, data }) => ({
        url: `/protrack/snags/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ProTrackSnag", "ProTrackDashboard"],
    }),
    deleteSnag: build.mutation<void, string>({
      query: (id) => ({ url: `/protrack/snags/${id}`, method: "DELETE" }),
      invalidatesTags: ["ProTrackSnag", "ProTrackDashboard"],
    }),

    // ── Handovers ──────────────────────────────────────────────────────────
    getHandovers: build.query<
      ProTrackHandover[],
      { blockId?: string; status?: string }
    >({
      query: ({ blockId, status }) => {
        const params = new URLSearchParams();
        if (blockId) params.set("blockId", blockId);
        if (status) params.set("status", status);
        const qs = params.toString();
        return qs ? `/protrack/handovers?${qs}` : "/protrack/handovers";
      },
      providesTags: ["ProTrackHandover"],
    }),
    createHandover: build.mutation<
      ProTrackHandover,
      Omit<ProTrackHandover, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/protrack/handovers", method: "POST", body }),
      invalidatesTags: ["ProTrackHandover", "ProTrackDashboard"],
    }),
    updateHandover: build.mutation<
      void,
      { id: string; data: Partial<ProTrackHandover> }
    >({
      query: ({ id, data }) => ({
        url: `/protrack/handovers/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ProTrackHandover", "ProTrackDashboard"],
    }),
    deleteHandover: build.mutation<void, string>({
      query: (id) => ({ url: `/protrack/handovers/${id}`, method: "DELETE" }),
      invalidatesTags: ["ProTrackHandover", "ProTrackDashboard"],
    }),

    // ── CTC Projects ───────────────────────────────────────────────────────
    getProjects: build.query<
      ProTrackProject[],
      { siteId?: string; overallStatus?: string }
    >({
      query: ({ siteId, overallStatus }) => {
        const params = new URLSearchParams();
        if (siteId) params.set("siteId", siteId);
        if (overallStatus) params.set("overallStatus", overallStatus);
        const qs = params.toString();
        return qs ? `/protrack/projects?${qs}` : "/protrack/projects";
      },
      providesTags: ["ProTrackProject"],
    }),
    getPortfolio: build.query<ProTrackPortfolio, void>({
      query: () => "/protrack/projects/portfolio",
      providesTags: ["ProTrackPortfolio"],
    }),
    createProject: build.mutation<
      ProTrackProject,
      Omit<ProTrackProject, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/protrack/projects", method: "POST", body }),
      invalidatesTags: [
        "ProTrackProject",
        "ProTrackPortfolio",
        "ProTrackDashboard",
      ],
    }),
    updateProject: build.mutation<
      void,
      { id: string; data: Partial<ProTrackProject> }
    >({
      query: ({ id, data }) => ({
        url: `/protrack/projects/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "ProTrackProject",
        "ProTrackPortfolio",
        "ProTrackDashboard",
      ],
    }),
    deleteProject: build.mutation<void, string>({
      query: (id) => ({ url: `/protrack/projects/${id}`, method: "DELETE" }),
      invalidatesTags: [
        "ProTrackProject",
        "ProTrackPortfolio",
        "ProTrackDashboard",
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProTrackDashboardQuery,
  useGetMilestonesQuery,
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useGetSnagsQuery,
  useCreateSnagMutation,
  useUpdateSnagMutation,
  useDeleteSnagMutation,
  useGetHandoversQuery,
  useCreateHandoverMutation,
  useUpdateHandoverMutation,
  useDeleteHandoverMutation,
  useGetProjectsQuery,
  useGetPortfolioQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} = protrackApi;
